import { CheckCircle2, Download, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { DoneSummary } from '../../stores/useAppStore';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { VideoExportButton } from './VideoExportButton';

export interface CompletionViewProps {
  readonly zipBlob: Blob;
  readonly zipName: string;
  readonly summary: DoneSummary;
  readonly onReset: () => void;
}

export function CompletionView(props: CompletionViewProps): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const u = URL.createObjectURL(props.zipBlob);
    setUrl(u);
    return (): void => URL.revokeObjectURL(u);
  }, [props.zipBlob]);

  const zipSizeMb = (props.zipBlob.size / (1024 * 1024)).toFixed(1);
  const elapsedMin = Math.floor(props.summary.elapsedMs / 1000 / 60);
  const elapsedSec = Math.floor((props.summary.elapsedMs / 1000) % 60);
  const mp4Name = useMemo(() => props.zipName.replace(/\.zip$/i, '.mp4'), [props.zipName]);

  return (
    <Card className="mx-auto max-w-xl">
      <CardContent className="space-y-6 p-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <CheckCircle2 className="text-status-success h-14 w-14" strokeWidth={1.5} aria-hidden />
          <h2 className="text-text-primary text-xl font-semibold tracking-tight">処理完了</h2>
          <p className="text-text-secondary text-sm">
            ZIP ファイルをダウンロードしてカーセンサーに流し込んでください
          </p>
        </div>

        <dl className="border-silver-tertiary bg-bg-secondary grid grid-cols-2 gap-3 rounded-md border p-4 text-xs">
          <Stat label="処理枚数" value={`${props.summary.totalPhotos} 枚`} />
          <Stat label="ZIP サイズ" value={`${zipSizeMb} MB`} />
          <Stat
            label="所要時間"
            value={`${String(elapsedMin).padStart(2, '0')}:${String(elapsedSec).padStart(2, '0')}`}
          />
          <Stat
            label="ヒーロー合成"
            value={props.summary.heroFallbackUsed ? 'フォールバック' : 'Gemini 成功'}
            emphasis={props.summary.heroFallbackUsed ? 'warning' : 'success'}
          />
          <Stat
            label="看板除去"
            value={
              props.summary.cleanedCount > 0
                ? `${props.summary.cleanedCount} 枚クリーン`
                : '対象なし'
            }
            {...(props.summary.cleanedCount > 0 ? { emphasis: 'success' as const } : {})}
          />
        </dl>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button asChild size="md" className="flex-1">
            <a href={url ?? '#'} download={props.zipName}>
              <Download className="mr-2 h-4 w-4" strokeWidth={1.5} aria-hidden />
              ZIP をダウンロード
            </a>
          </Button>
          <Button variant="secondary" size="md" onClick={props.onReset} type="button">
            <RotateCcw className="mr-2 h-4 w-4" strokeWidth={1.5} aria-hidden />
            新しい車両を処理
          </Button>
        </div>

        <VideoExportButton zipBlob={props.zipBlob} mp4Name={mp4Name} />
      </CardContent>
    </Card>
  );
}

function Stat(props: {
  readonly label: string;
  readonly value: string;
  readonly emphasis?: 'success' | 'warning';
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-text-tertiary text-[10px] font-medium tracking-wider uppercase">
        {props.label}
      </dt>
      <dd
        className={
          props.emphasis === 'warning'
            ? 'text-status-warning text-sm font-semibold tabular-nums'
            : props.emphasis === 'success'
              ? 'text-status-success text-sm font-semibold tabular-nums'
              : 'text-text-primary text-sm font-semibold tabular-nums'
        }
      >
        {props.value}
      </dd>
    </div>
  );
}
